import { PropsWithChildren, createContext, useEffect, useState } from "react";

import { LS_SIDEBAR_COLLAPSED } from "../../utils/common";

const standardWidth = "1024px";
const fullWidth = "100%";
const mobileWidth = 768;

export const LayoutContext = createContext({
  sidebarIsOpen: false,
  contentWidth: standardWidth,
  sidebarIsCollapsed: false,
  toggleSidebar: () => {},
  closeSidebar: () => {},
  openSidebar: () => {},
  setFullContentWidth: () => {},
  setStandardContentWidth: () => {},
  toggleCollapsedSidebar: () => {},
});

export const LayoutContextProvider = ({ children }: PropsWithChildren) => {
  const [sidebarIsOpen, setSidebarIsOpen] = useState(false);
  const [contentWidth, setContentWidth] = useState(standardWidth);
  const [sidebarIsCollapsed, setSidebarIsCollapsed] = useState(() => {
    const savedValue = localStorage.getItem(LS_SIDEBAR_COLLAPSED);
    return savedValue ? JSON.parse(savedValue) : false;
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < mobileWidth) {
        setSidebarIsCollapsed(false);
        localStorage.setItem(LS_SIDEBAR_COLLAPSED, "false");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => {
    setSidebarIsOpen(!sidebarIsOpen);
  };

  const closeSidebar = () => {
    setSidebarIsOpen(false);
  };

  const openSidebar = () => {
    setSidebarIsOpen(true);
  };

  const setFullContentWidth = () => {
    setContentWidth(fullWidth);
  };

  const setStandardContentWidth = () => {
    setContentWidth(standardWidth);
  };

  const toggleCollapsedSidebar = () => {
    setSidebarIsCollapsed((prev: boolean) => {
      localStorage.setItem(LS_SIDEBAR_COLLAPSED, (!prev).toString());
      return !prev;
    });
  };

  return (
    <LayoutContext.Provider
      value={{
        sidebarIsOpen,
        contentWidth,
        sidebarIsCollapsed,
        toggleSidebar,
        closeSidebar,
        openSidebar,
        setFullContentWidth,
        setStandardContentWidth,
        toggleCollapsedSidebar,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};
